import { useAtom } from "jotai";
import { useEffect, useState } from "react";

import { debugHudAtom } from "@/core/state";
import { Carte } from "@/shared/ui";

interface Sample {
  name: string;
  duration: number;
  ts: number;
}

const ROLLING_WINDOW = 30;

/**
 * Floating perf HUD. Shows rolling p50/p95 of last 30 fetch operations
 * to any URL containing `/api/`. Activated by `?debug=1` URL param
 * (also persisted in localStorage via `debugHudAtom`).
 *
 * Adapted from `simscope/src/components/PerfHUD.tsx`. The implementation
 * piggybacks on `PerformanceObserver` resource entries — no fetch wrapping.
 */
export function PerfHUD() {
  const [enabled, setEnabled] = useAtom(debugHudAtom);
  const [samples, setSamples] = useState<Sample[]>([]);

  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get("debug") === "1") setEnabled(true);
  }, [setEnabled]);

  useEffect(() => {
    if (!enabled || typeof PerformanceObserver === "undefined") return;
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.entryType !== "resource") continue;
        const r = entry as PerformanceResourceTiming;
        if (!r.name.includes("/api/")) continue;
        setSamples((prev) =>
          [
            ...prev,
            { name: r.name, duration: r.duration, ts: Date.now() },
          ].slice(-ROLLING_WINDOW)
        );
      }
    });
    observer.observe({ entryTypes: ["resource"] });
    return () => observer.disconnect();
  }, [enabled]);

  if (!enabled || samples.length === 0) return null;

  const sorted = samples.map((s) => s.duration).sort((a, b) => a - b);
  const p50 = sorted[Math.floor(sorted.length * 0.5)] ?? 0;
  const p95 = sorted[Math.floor(sorted.length * 0.95)] ?? 0;

  return (
    <Carte remplissage="sm" className="fixed bottom-2 right-2 z-50 w-auto font-mono text-[12px]">
      n={samples.length} p50={Math.round(p50)}ms p95={Math.round(p95)}ms
    </Carte>
  );
}
